import appPath from './appPath';
export function isStringBlank(str) {
  if (str == undefined) {
    return true;
  }
  let testExp = new RegExp("(^$|^null$|^undefined$|^[ ]+$)");
  return testExp.test(str);
}

export function isWebWorkerSupported() {
  return !!window.Worker;
}

export function setCookie(cname, cvalue, exdays) {
  let d = new Date();
  exdays = parseFloat(exdays);
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires =
    exdays == -1
      ? "expires=Thu, 01 Jan 1970 00:00:01 GMT"
      : "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + "; " + expires + ";path=/";
}

export function getCookie(cname) {
  let name = cname + "=";
  let ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") c = c.substring(1);
    if (c.indexOf(name) == 0) return c.substring(name.length, c.length);
  }
  return "";
}

export function routeToLogin() {
  // Clear legacy cookies
  setCookie("userToken", "", -1);
  setCookie("userId", "", -1);
  // Clear OIDC state from BOTH stores. The signed-in user now lives in
  // localStorage (shared across tabs — see auth/oidcConfig), so clearing only
  // sessionStorage would leave the session behind and the login page would
  // bounce straight back in.
  [window.sessionStorage, window.localStorage].forEach((store) => {
    try {
      Object.keys(store)
        .filter((k) => k.startsWith('oidc.'))
        .forEach((k) => store.removeItem(k));
    } catch { /* storage unavailable — nothing to clear */ }
  });
  // origin, not protocol+hostname: the latter drops the port, so any non-443
  // host (localhost:3000) navigated to a URL that doesn't exist.
  window.location.replace(window.location.origin + appPath('/login'))
}

export const DEFAULT_ERR_MSG =
  "Something went wrong, Please try again, if error persists, Please reach out to support team!";

// Shown when axios came back with NO response at all — the request never
// reached the server (connection reset, offline tab, CORS block, aborted
// navigation). Kept distinct from DEFAULT_ERR_MSG on purpose: those failures
// leave zero trace in the gateway access log, so when both cases read the same
// a support screenshot cannot tell us which happened. Bit us 2026-09-09, when a
// customer's Send SMS toast had no matching request in envoy anywhere.
export const NETWORK_ERR_MSG =
  "Could not reach the server. Please check your internet connection and try again.";

export const isBadReq = (data) =>
  data?.response?.status == 404 && data.response.data?.success == false;

export const handleRejectCatch = (dispatch, data, type) => {
  if (isBadReq(data)) {
    routeToLogin();
  } else {
    dispatch({ type, payload: data?.response?.data || {} });
  }
};

export const getAuthToken = () => {
  // Check for active impersonation token first
  try {
    const imp = sessionStorage.getItem('combird_impersonation');
    if (imp) {
      const { accessToken } = JSON.parse(imp);
      if (accessToken) return `Bearer ${accessToken}`;
    }
  } catch { /* ignore parse errors */ }

  // Try OIDC token first (shared localStorage), fall back to legacy cookie
  try {
    const {getOidcAccessToken} = require('../auth/tokenUtils');
    const oidcToken = getOidcAccessToken();
    if (oidcToken) return `Bearer ${oidcToken}`;
  } catch {
    // auth module not available, use legacy
  }
  return `Bearer ${getCookie("userToken")}`;
};

export const handleAPIDispatch = (request, type) => {
  return (dispatch) => {
    request
      .then(({ data }) => {
        dispatch({ type, payload: data });
      })
      .catch((data) => {
        handleRejectCatch(dispatch, data, type);
      });
  };
};

export function mobileValidator(num) {
  return /^[3-9]\d{9}$/.test(num);
}

export function emailValidator(email) {
  return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  );
}

export function numPad(num, size, padVal) {
  let numString = num + "";
  while (numString.length < size) {
    numString = padVal + numString;
  }
  return numString;
}

function _getFormattedDate(date) {
  let year = date.getFullYear();
  year = year.toString().substring(2);

  let month = (1 + date.getMonth()).toString();
  month = month.length > 1 ? month : "0" + month;

  let day = date.getDate().toString();
  day = day.length > 1 ? day : "0" + day;

  return {
    date,
    day,
    month,
    year,
  };
}

export function spaceSeperatedDate(date) {
  let { day, month, year } = _getFormattedDate(date);
  return `${day}-${month}-${year}`;
}

// Parse timestamp in milliseconds (as string or number) to Date
export function parseTimestampMs(timestamp) {
  if (!timestamp) return null;
  const ms = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
  if (isNaN(ms) || ms === 0) return null;
  return new Date(ms);
}

// Format timestamp in milliseconds to readable date string
export function formatTimestampMs(timestamp, isMobile, shortView) {
  const date = parseTimestampMs(timestamp);
  if (!date || isNaN(date.getTime())) return '--';
  return dateFormat(date, isMobile, shortView);
}

export function dateFromUnixEpoch(secondStr) {
  let seconds = parseInt(secondStr);
  let milliSeconds = seconds * 1000;
  let dateTime = new Date(milliSeconds);
  return dateTime;
}

export function dateFormat(dateVal, isMobile, shortView) {
  let mon = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let date = new Date(dateVal);
  let AP = "AM";
  let MM = numPad(date.getMinutes(), 2, 0);
  let ss = numPad(date.getSeconds(), 2, 0);
  let hh = date.getHours();
  let fff = date.getMilliseconds();
  let HH = hh;
  if (hh > 11) {
    HH = hh % 12;
    AP = "PM";
  }
  if (HH == 0) {
    HH = 12;
  }
  HH = numPad(HH, 2, 0);
  hh = numPad(hh, 2, 0);
  let dd = numPad(date.getDate(), 2, 0);
  let mm = date.getMonth();
  let mmm = mon[mm];
  mm = numPad(mm + 1, 2, 0);
  let yyyy = date.getFullYear();
  let yy = numPad(yyyy % 100, 2, 0);
  if (isMobile) {
    if (shortView) {
      return (
        dd + " " + mmm + "'" + yy + ", " + HH + ":" + MM + ":" + ss + " " + AP
      );
    } else {
      return dd + "/" + mm + "/" + yy;
    }
  } else {
    return (
      dd + " " + mmm + " " + yyyy + ", " + HH + ":" + MM + ":" + ss + " " + AP
    );
  }
}

const OLDCurrentLocalStorage = "epadhaiBaseData"; // this will be current for existing user
// Exported for the cross-tab 'storage' listener in SessionIdentityGuard —
// the shared app-profile key that must always describe the SAME user as the
// per-tab OIDC token.
export const NewLocalStorage = "epadhaiBaseData_v2";
export function addToLocalStorage(data) {
  try {
    window.localStorage.setItem(NewLocalStorage, JSON.stringify(data));
  } catch (err) {
    routeToLogin();
  }
  // window.localStorage.setItem(OLDCurrentLocalStorage,JSON.stringify(data));
}

export function getFromLocalStorage() {
  try {
    let data = JSON.parse(window.localStorage.getItem(OLDCurrentLocalStorage));
    if (data) {
      window.localStorage.removeItem(OLDCurrentLocalStorage);
      routeToLogin();
      return data;
    } else {
      data = JSON.parse(window.localStorage.getItem(NewLocalStorage));
    }
    return data;
  } catch (err) {
    routeToLogin();
  }
}

export function removeFromLocalStorage() {
  try {
    window.localStorage.removeItem(NewLocalStorage);
  } catch (err) {
    routeToLogin();
  }
}

export function isGroupAvaible(gId) {
  let groupData = (getFromLocalStorage() || {}).groupData;
  if (!Array.isArray(groupData)) return false;
  for (let group of groupData) {
    if (group.id == gId) {
      return true;
    }
  }
  return false;
}

export function isURLValid(url) {
  const re =
    /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/;
  return re.test(url);
}

export function sortGroup(list) {
  if (!list || !Array.isArray(list)) return [];
  return [...list].sort((first, second) =>
    first.name.toLowerCase() > second.name.toLowerCase() ? 1 : -1,
  );
}

export function getDynamicPlaceholderSlots(text) {
  if (!text) {
    return [];
  }

  const matches = [...text.matchAll(/\{#([a-zA-Z0-9_]+)#\}/g)] || [];
  const occurrences = {};
  const totalByName = {};

  matches.forEach((match) => {
    const name = match[1];
    totalByName[name] = (totalByName[name] || 0) + 1;
  });

  return matches.map((match) => {
    const name = match[1];
    const slotCount = (occurrences[name] || 0) + 1;
    occurrences[name] = slotCount;

    return {
      name,
      slotCount,
      displayName:
        totalByName[name] > 1
          ? `${formatDynamicPlaceholderLabel(name)} ${slotCount}`
          : formatDynamicPlaceholderLabel(name),
    };
  });
}

export function formatDynamicPlaceholderLabel(name) {
  if (!name) {
    return "Variable";
  }
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return "Variable";
  }

  return words
    .map((word) => {
      if (word === word.toUpperCase() && word.length <= 4) {
        return word.toUpperCase();
      }
      if (word.length <= 2) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function replaceDynamicPlaceholderTokens(text, values) {
  if (!text) {
    return text;
  }

  if (Array.isArray(values)) {
    const replacementValues = [...values];
    return text.replace(/\{#([a-zA-Z0-9_]+)#\}/g, (match) => {
      const nextValue = replacementValues.shift();
      return nextValue !== undefined && nextValue !== null
        ? String(nextValue)
        : match;
    });
  }

  return text.replace(/\{#([a-zA-Z0-9_]+)#\}/g, (match, name) => {
    return values[name] !== undefined ? values[name] : match;
  });
}

export function isNotValidPromoTime() {
  const date = new Date();
  return (
    date.getHours() <= 10 ||
    date.getHours() >= 21 ||
    (date.getHours() >= 20 && date.getMinutes() >= 55)
  );
}

export const convertToLocale = (num) => {
  if (!num) {
    return "0";
  }
  return num.toLocaleString("en-IN");
};

const convertToNormal = (num) => convertToLocale(parseFloat(num));

export const localeNumber = (number, percentage = false) => {
  try {
    return percentage
      ? parseFloat(parseFloat(convertToNormal(number)).toFixed(2))
      : convertToNormal(number);
  } catch (error) {
    return number;
  }
};

const convertToNumber = (num, conversionValue) => {
  return convertToLocale(
    parseFloat(parseFloat(num / conversionValue).toFixed(2)),
  );
};

const convertToLakh = (num) => `${convertToNumber(num, 100000)}L`;

const convertToThousand = (num) => `${convertToNumber(num, 1000)}T`;

export const formatNumber = (number) => {
  try {
    let num = 0;

    if (number >= 100000) {
      num = convertToLakh(number);
    } else if (number < 100000 && number > 999) {
      num = convertToThousand(number);
    } else {
      num = convertToNormal(number);
    }

    return num;
  } catch (error) {
    return number;
  }
};

export const validateLoginRequest = (param) => {
  let errorJson = {
    userIdMobile: "",
    password: "",
  };
  let passValidation = true;
  if (isStringBlank(param.userIdMobile)) {
    errorJson.userIdMobile = "Please enter User Id/Mobile No";
    passValidation = false;
  }
  if (isStringBlank(param.password)) {
    errorJson.password = "Please enter Password";
    passValidation = false;
  }
  return { isValidated: passValidation, errorJson: errorJson };
};

export const addGroupListInLocal = (data) => {
  let currentData = getFromLocalStorage();
  const groupData = data?.data?.groups || data?.data?.baseData?.groupData;
  if (currentData) {
    currentData.groupData = groupData;
  } else {
    currentData = { groupData };
  }
  addToLocalStorage(currentData);
};
